<template>
  <div>
    <h3>General description</h3>
    Different types of analysis are available and listed below:
    <ul>
      <li>
        <b>Revisit</b>: revisit metrics of the points of interest are computed.
      </li>
      <li>
        <b>Ground segment simplified</b>: additional metrics related to the
        ground stations are computed.
      </li>
      <li>
        <b>Ground segment extended</b>: more complete metrics related to the
        ground stations are computed.
      </li>
    </ul>
    <br />
    <h3>Revisit metrics</h3>
    <p>The revisit metrics are related to the points of interest as follows:</p>
    <ul>
      <li>
        <b>Access duration</b>: duration during which a point is continuously
        visible by the constellation.
      </li>
      <li>
        <b>Revisit time</b>: time elapsed between two visibility intervals of a
        point of interest.
      </li>
      <li>
        <b>Number of accesses</b>: number of times a point is seen by any
        satellite of the constellation.
      </li>
      <li>
        <b>Collect time</b>: time elapsed between a message emitted at a random
        date and the next visibility interval. The message date is modelled as a
        continuous uniform distribution, from which the collect time statistics
        are calculated analytically.
      </li>
      <li>
        <b>Number of spacecrafts simultaneously seen</b>: number of spacecrafts
        that a point sees at a given moment.
      </li>
      <v-img
        contain
        :src="
          require('../../../assets/images/tooltip-constellation-revisit-metrics.png')
        "
        aspect-ratio="1.6"
      />
    </ul>

    <br />
    <h3>Ground segment simplified metrics</h3>
    <p>The metrics related to the ground stations are the following:</p>
    <ul>
      <li>
        <b>Forward link transfer time</b>: time elapsed between the last
        possible data exchange with a ground station and the visibility of a
        point of interest.
      </li>
      <li>
        <b>Reverse link transfer time</b>: time elapsed between the visibility
        of a point of interest and the next possible data exchange with a ground
        station.
      </li>
      <li>
        <b>Forward to reverse link time</b>: sum of the forward and the reverse
        link transfer times. It is the time elapsed between an order and the
        corresponding data transmission to the ground.
      </li>
      <v-img
        contain
        :src="
          require('../../../assets/images/tooltip-constellation-outputs.png')
        "
        aspect-ratio="1.6"
      />
    </ul>

    <br />
    <h3>Ground segment extended metrics</h3>
    <p>
      In addition to the metrics of the "Ground segment simplified" analysis,
      the metrics listed below are also computed in this case:
    </p>
    <ul>
      <li>
        <b>Time between reverse link arrivals</b>: time elapsed between two
        consecutive data transmissions to the ground segment with respect to a
        point of interest.
      </li>
      <v-img
        contain
        :src="
          require('../../../assets/images/tooltip-constellation-time-between-reverse-link-arrivals.png')
        "
        aspect-ratio="1.6"
      />
    </ul>

    <h3>Metrics statistics</h3>
    <p>
      For each output (performance metric) the minimum, average and maximum
      values are computed on the ensemble of data. The median is also returned
      in order to evaluate the impact of extreme values on the average.
      Additionally, the 50-th, 75-th, 90-th and 95-th percentiles are computed
      to evaluate the results in different levels of majority of the cases.
    </p>
    <p>
      <b>Access duration percentiles</b>: for the access duration metric (and
      only this metric), the percentiles values are inverted. For example, if
      the 90-th percentile of the access duration is equal to 1 minute, it means
      that 90% of the revisits on that point have a duration greater than 1
      minute, and not the other way round.
    </p>
    <p>
      <b>Custom percentile</b>: the user can specify a custom value
      (<katex-element expression="p \in [0, 100]" />) to compute the
      <katex-element expression="p" />-th percentile.
    </p>
  </div>
</template>

<script>
export default {
  name: 'tooltipRevisitGroundSegments'
}
</script>
